import { type Board, type BoardSource, boardSchema } from "@kourai/api/home-board";

/**
 * The local copy of each board (one per person per workspace), so the new tab paints the board
 * before any network request. localStorage, not chrome.storage: it's synchronous (no flash of the
 * default board), and the same code runs in the extension and the web app at `/`.
 */

export type CachedBoard = {
  board: Board;
  /** The server version this copy is based on (0: never saved). */
  version: number;
  source: BoardSource;
  /** Edited here and not saved yet. */
  dirty?: boolean;
  /**
   * The board as the server last had it (at `version`), which local edits are replayed against
   * when another device saved in between (ADMIN-259).
   */
  base?: Board;
};

const PREFIX = "kourai-home-board";

export const cacheKey = (userId: string, workspaceId: string) =>
  `${PREFIX}:${userId}:${workspaceId}`;

function defaultStorage(): Storage | undefined {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
}

export function readCachedBoard(
  userId: string,
  workspaceId: string,
  storage = defaultStorage(),
): CachedBoard | null {
  try {
    const raw = storage?.getItem(cacheKey(userId, workspaceId)) ?? null;
    return parseCachedBoard(raw);
  } catch {
    return null;
  }
}

/** A cached board from its stored JSON (also what another tab's `storage` event carries). */
export function parseCachedBoard(raw: string | null): CachedBoard | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CachedBoard;
    const board = boardSchema.safeParse(parsed.board);
    if (!board.success || typeof parsed.version !== "number") return null;
    const base = parsed.base ? boardSchema.safeParse(parsed.base) : null;
    return { ...parsed, board: board.data, base: base?.success ? base.data : undefined };
  } catch {
    return null;
  }
}

export function writeCachedBoard(
  userId: string,
  workspaceId: string,
  value: CachedBoard,
  storage = defaultStorage(),
) {
  try {
    storage?.setItem(cacheKey(userId, workspaceId), JSON.stringify(value));
  } catch {
    // Storage full or unavailable: the board still saves to the server.
  }
}
